import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Imprint } from '../types/Imprint';
import { LocalizedText, emptyLocalizedText } from '../types/LocalizedText';
import { LocalizedFieldsTabs } from '../components/common/LocalizedTextFields';
import { useLanguages } from '../hooks/useLanguages';
import {
  getMissingRequiredLanguageLabels,
  normalizeLocalizedText,
  serializeLocalizedText,
} from '../utils/localizedText';

const ImprintPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { activeLanguages, languageCodes } = useLanguages();
  const [title, setTitle] = useState<LocalizedText>(emptyLocalizedText());
  const [content, setContent] = useState<LocalizedText>(emptyLocalizedText());
  const [formError, setFormError] = useState('');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const imprintFields = useMemo(
    () => [
      {
        key: 'title',
        label: t('imprint.pageTitle'),
        required: true,
      },
      {
        key: 'content',
        label: t('imprint.pageContent'),
        multiline: true,
        rows: 16,
        required: true,
        htmlAllowed: true,
      },
    ],
    [t]
  );

  const { data: imprint, isLoading, error } = useQuery({
    queryKey: ['imprint'],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/imprint`);
      return response.data as Imprint;
    },
  });

  useEffect(() => {
    if (imprint) {
      setTitle(normalizeLocalizedText(imprint.title, languageCodes));
      setContent(normalizeLocalizedText(imprint.content, languageCodes));
    }
  }, [imprint, languageCodes]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return axios.put(`${process.env.REACT_APP_API_URL}/imprint`, {
        title: serializeLocalizedText(title, activeLanguages),
        content: serializeLocalizedText(content, activeLanguages),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imprint'] });
      setFormError('');
      setNotification({
        open: true,
        message: t('imprint.saved'),
        severity: 'success',
      });
    },
    onError: (err: unknown) => {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setFormError(axiosError.response?.data?.error || t('imprint.saveFailed'));
    },
  });

  const handleSave = () => {
    const missingTitle = getMissingRequiredLanguageLabels(title, activeLanguages);
    if (missingTitle.length > 0) {
      setFormError(
        t('localizedText.missingRequiredLanguages', { languages: missingTitle.join(', ') })
      );
      return;
    }

    const missingContent = getMissingRequiredLanguageLabels(content, activeLanguages);
    if (missingContent.length > 0) {
      setFormError(
        t('localizedText.missingRequiredLanguages', { languages: missingContent.join(', ') })
      );
      return;
    }

    setFormError('');
    saveMutation.mutate();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{t('imprint.errorLoading')}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {t('imprint.title')}
      </Typography>

      <Paper sx={{ p: 3 }}>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <LocalizedFieldsTabs
            fields={imprintFields}
            values={{ title, content }}
            onChange={(updatedValues) => {
              if (updatedValues.title) {
                setTitle(updatedValues.title);
              }
              if (updatedValues.content) {
                setContent(updatedValues.content);
              }
            }}
          />

          {imprint?.updatedAt && (
            <Typography variant="caption" color="text.secondary">
              {t('imprint.lastUpdated', {
                date: new Date(imprint.updatedAt).toLocaleString(),
              })}
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? t('imprint.saving') : t('common.save')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImprintPage;
